/**
 * REVIEW-OBS-2 (2026-07-03) — drop review issues the repair passes already fixed.
 *
 * The quality pipeline's best-of selector picks a draft, then the voice-repair
 * and grounding-repair passes rewrite it. The persisted `qualityReview.issues`
 * describe the PRE-repair draft — so an issue the repair resolved ships as if
 * unresolved (live: vrhond's "400 MW final build-out" issue survived into the
 * report doc although no "400 MW" exists in the final text).
 *
 * Survival rule (deliberately asymmetric — keep on any doubt):
 *   An issue is dropped ONLY when
 *     (a) it contains an ATTRIBUTION: a report-ish subject (report / section /
 *         narrative / recommendation / it) followed within a short window by a
 *         stating verb (states / claims / invents / says / asserts / suggests /
 *         reports / names / identifies / describes), and
 *     (b) an OFFENDING TOKEN — the first quoted span or number+unit after that
 *         verb — can be extracted, and
 *     (c) that token no longer appears in the final report text (normalized).
 *   Everything else survives: issues with no extractable specifics, issues
 *   whose quoted span is the EXPECTED value attributed to the SED/canonical
 *   ("The SED identifies it as 'GILLIS'" — subject is evidence-ish, not
 *   report-ish), and issues whose offending token is still present.
 */

export interface ReviewIssueLike {
  section?: string;
  issue?: string;
  description?: string;
}

/** Report-ish attribution subjects — the sentence is describing the REPORT's text. */
const REPORT_SUBJECT_RE =
  /\b(?:report|section|subsection|narrative|recommendation|summary|study|pathway|text|prose|passage|description|entry|stage|paragraph|it)\b[^.]{0,40}?\b(states|claims|invents|says|asserts|suggests|reports|names|identifies|describes|prints|lists|shows)\b/i;

/** Evidence-ish subjects — the nearby quote is the EXPECTED value, not the defect. */
const EVIDENCE_SUBJECT_RE = /\b(?:SED|canonical|evidence|fact[s]?|source|document)\b[^.]{0,30}\b(?:states|identifies|says|defines|shows)\b/i;

const QUOTED_SPAN_RE = /(?:"([^"”]{2,400})"|“([^"”]{2,400})”|['‘]((?:[^'’]|(?<=\w)['’](?=\w)){2,400})['’])/;
const NUMBER_UNIT_RE = /\$?\d[\d,.]*(?:\s*[-–—]\s*\$?\d[\d,.]*)?\s*(?:million|billion|MW|MWe|kV|km|mi|miles|acres?|MGD|%)/i;

function normalize(s: string): string {
  return s
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/[‘’“”]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function readStringAtFieldPath(target: unknown, fieldPath: string): string | null {
  if (!target || typeof target !== 'object') return null;
  const keys = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  if (keys.length === 0) return null;
  let node: unknown = target;
  for (const key of keys) {
    if (!node || typeof node !== 'object' || !Object.hasOwn(node, key)) return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === 'string' ? node : null;
}

const TOP_LEVEL_REPORT_FIELD_RE =
  /\b(executiveSummary|projectIntentOverview|powerAnalysis|connectivityAnalysis|environmentalAnalysis|sitingNotes|btmDecisionSummary)\b/g;

export function extractExplicitFieldPathsFromReviewIssue(issueText: string): string[] {
  if (!issueText || typeof issueText !== 'string') return [];
  const out: string[] = [];
  for (const match of issueText.matchAll(/\b([A-Za-z][A-Za-z0-9_]*(?:\[\d+\]|\.[A-Za-z0-9_]+)+)\b/g)) {
    const clean = match[1]!.replace(/[.,;:)\]'"”’]+$/, '').replace(/\[(\d+)\]/g, '.$1');
    if (!clean || /^sed\./i.test(clean)) continue;
    if (!out.includes(clean)) out.push(clean);
  }
  for (const match of issueText.matchAll(TOP_LEVEL_REPORT_FIELD_RE)) {
    const fieldName = match[1]!;
    if (!out.includes(fieldName) && !out.some((p) => p.startsWith(`${fieldName}.`))) {
      out.push(fieldName);
    }
  }
  if (
    /\bExecutive\s+Summary\b/i.test(issueText) &&
    !out.includes('executiveSummary') &&
    !out.some((p) => p.startsWith('executiveSummary.'))
  ) {
    out.push('executiveSummary');
  }
  return out;
}

function resolveTargetFieldTextsForReviewIssue(
  issueText: string,
  fallbackText: string,
  report?: unknown,
): string[] | null {
  const explicitPaths = extractExplicitFieldPathsFromReviewIssue(issueText);
  if (explicitPaths.length === 0) {
    return fallbackText ? [fallbackText] : null;
  }
  if (report && typeof report === 'object') {
    const resolvedSegments: string[] = [];
    for (const p of explicitPaths) {
      const val = readStringAtFieldPath(report, p);
      if (val === null) return null;
      resolvedSegments.push(val);
    }
    return resolvedSegments;
  }
  const leadingPathMatch = /^\s*([A-Za-z][A-Za-z0-9_]*(?:\[\d+\]|\.[A-Za-z0-9_]+)*)\s*:\s*([\s\S]*)$/.exec(
    fallbackText,
  );
  if (leadingPathMatch) {
    const normalizedLeading = leadingPathMatch[1]!.replace(/\[(\d+)\]/g, '.$1');
    if (explicitPaths.includes(normalizedLeading)) {
      if (explicitPaths.length > 1) return null;
      return [leadingPathMatch[2]!];
    }
    if (
      normalizedLeading.includes('.') ||
      TOP_LEVEL_REPORT_FIELD_RE.test(normalizedLeading)
    ) {
      TOP_LEVEL_REPORT_FIELD_RE.lastIndex = 0;
      return null;
    }
    TOP_LEVEL_REPORT_FIELD_RE.lastIndex = 0;
  }
  if (explicitPaths.length > 1) {
    return null;
  }
  return fallbackText ? [fallbackText] : null;
}

function resolveTargetTextForReviewIssue(
  issueText: string,
  fallbackText: string,
  report?: unknown,
): string | null {
  const segments = resolveTargetFieldTextsForReviewIssue(issueText, fallbackText, report);
  if (!segments || segments.length === 0) return null;
  return segments.join('\n');
}

/**
 * Extract the offending token an issue attributes to the report, or null when
 * no high-confidence extraction is possible (⇒ the issue must survive).
 */
export function extractOffendingToken(issueText: string): string | null {
  if (!issueText) return null;
  const m = REPORT_SUBJECT_RE.exec(issueText);
  if (!m) return null;
  // Search only AFTER the attribution verb, and stop at the sentence end so a
  // trailing "…which contradicts the 300 MW intent" (the EXPECTED value) is
  // never mistaken for the offending token.
  const afterVerb = issueText.slice(m.index + m[0].length);
  const sentenceEnd = afterVerb.search(/[.;](?:\s|$)/);
  let scope = sentenceEnd >= 0 ? afterVerb.slice(0, sentenceEnd) : afterVerb;
  // Inside the scope, a contrast clause introduces the expected value — cut it.
  const contrast = scope.search(/\b(?:which contradicts|contradicting|instead of|rather than|whereas|but the SED|while the SED|however)\b/i);
  if (contrast >= 0) scope = scope.slice(0, contrast);
  // If the scope itself pivots to an evidence subject, don't extract from it.
  if (EVIDENCE_SUBJECT_RE.test(scope)) {
    const evIdx = scope.search(EVIDENCE_SUBJECT_RE);
    scope = scope.slice(0, evIdx);
  }
  const quoted = QUOTED_SPAN_RE.exec(scope);
  if (quoted) return quoted[1] ?? quoted[2] ?? quoted[3] ?? null;
  const num = NUMBER_UNIT_RE.exec(scope);
  if (num) return num[0];
  return null;
}

function splitIntoSentences(text: string): string[] {
  const cleaned = text.replace(/<!--[\s\S]*?-->/g, ' ').trim();
  if (!cleaned) return [];
  return cleaned
    .split(/(?<=[.!?])\s+(?=[A-Z<\[("‘“0-9])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function hasAffirmativeApprovalOrArrangementAssertion(normText: string): boolean {
  // 1. Direct "securing/secure/secured ... approvals/permits/arrangements" unless preceded by negation/contrast.
  const clauses = normText.split(/[;]|,\s+(?:and|but|while|whereas)\s+/);
  for (const rawClause of clauses) {
    const clause = rawClause.trim();
    if (!clause) continue;
    const hasNegation =
      /\b(?:neither(?:\s+source)?|none|no\s+source|does\s+not|do\s+not|did\s+not|cannot|can\s+not|not|never|without|rather\s+than|instead\s+of|before\s+relying\s+on|confirm\s+whether|verify\s+whether)\b/.test(
        clause,
      );
    if (
      !hasNegation &&
      /\b(?:securing|secure|secured|obtaining|obtained|granting|granted)\s+(?:[a-z0-9,/-]+\s+){0,4}(?:approvals?|permits?|water\/wastewater\s+arrangements?|water\s+and\s+wastewater\s+arrangements?|water\s+supply\s+and\s+wastewater\s+(?:discharge\s+)?arrangements?|service\s+arrangements?|service\s+commitments?)\b/.test(
        clause,
      )
    ) {
      return true;
    }
    if (
      !hasNegation &&
      /\b(?:establishes|establish|established|confirms|confirmed|guarantees|guaranteed)\s+(?:[a-z0-9,/-]+\s+){0,5}(?:project\s+)?(?:permits?|approvals?|water\s+rights?|water\/wastewater\s+arrangements?|water\s+and\s+wastewater\s+arrangements?|water\s+supply|wastewater|service\s+commitments?|service\s+arrangements?)\b/.test(
        clause,
      )
    ) {
      return true;
    }
    if (
      !hasNegation &&
      /\bis\s+a\s+confirmed\s+(?:project\s+)?authority\b/.test(clause)
    ) {
      return true;
    }
  }
  return false;
}

function areSecondaryComplaintObligationsSatisfied(
  issueText: string,
  scopedText: string,
): boolean {
  const haystack = normalize(scopedText);
  if (!haystack) return false;

  // Obligation B: If complaint warns against asserting securing approvals or arrangements,
  // reject any affirmative assertion of securing/establishing approvals or arrangements.
  if (
    /\b(?:assert\s+securing\s+approvals|securing\s+approvals\s+and\s+water\/wastewater\s+arrangements)\b/i.test(
      issueText,
    )
  ) {
    if (hasAffirmativeApprovalOrArrangementAssertion(haystack)) {
      return false;
    }
  }

  // Obligation C1: If complaint requires framing as "conditional screening diligence" / "screening diligence",
  // require both "screening" and "diligence" plus conditional/unresolved/non-establishing qualification.
  if (/\bscreening\s+diligence\b/i.test(issueText)) {
    if (!/\bscreening\b/.test(haystack) || !/\bdiligence\b/.test(haystack)) {
      return false;
    }
    if (
      !/\b(?:unresolved|conditional|screening\s+inputs?\s+only|neither\s+source\s+establishes|does\s+not\s+establish|do\s+not\s+establish)\b/.test(
        haystack,
      )
    ) {
      return false;
    }
  } else if (/\bconditional\s+diligence\b/i.test(issueText)) {
    if (
      !/\b(?:confirm\s+whether|evaluate\s+permit\s+requirements|conditional|diligence|unresolved|verify\s+applicable)\b/.test(
        haystack,
      )
    ) {
      return false;
    }
  }

  // Obligation C2: If complaint specifically identifies cited [Ref N] references used to assert
  // approvals/arrangements rather than conditional screening diligence, every cited [Ref N] must
  // remain present in the targeted text AND be framed as screening/verification inputs rather than
  // established approvals or arrangements.
  const citedRefs = Array.from(issueText.matchAll(/\[\s*Ref\s+(\d+)\s*\]/gi), (m) => m[1]!);
  if (citedRefs.length > 0) {
    const sentences = splitIntoSentences(scopedText);
    for (const refNum of citedRefs) {
      const refRe = new RegExp(`\\[(?:(?:verified|inferred)\\s+)?Ref\\s+${refNum}\\]`, 'i');
      if (!refRe.test(scopedText)) {
        return false;
      }
      const matchingSentences = sentences.filter((s) => refRe.test(s));
      if (matchingSentences.length === 0) {
        return false;
      }
      for (const sent of matchingSentences) {
        const normSent = normalize(sent);
        if (hasAffirmativeApprovalOrArrangementAssertion(normSent)) {
          return false;
        }
        if (
          /\bscreening\s+diligence\b/i.test(issueText) &&
          !/\b(?:screening\s+inputs?|screening\s+diligence|conditional\s+screening|unresolved\s+screening|neither\s+source\s+establishes|does\s+not\s+establish|do\s+not\s+establish)\b/.test(
            normSent,
          )
        ) {
          return false;
        }
      }
    }
  }

  return true;
}

/**
 * Detects when a pre-repair reviewer issue complains that an entity was named
 * "without hedging it as an unverified AI-research..." (or asserts "securing
 * approvals" / misuses citations rather than conditional screening diligence),
 * and the repaired report text in the target field satisfies all obligations:
 *   1. Every occurrence of the named entity in the target field is hedged
 *      within its own sentence/clause as an unverified AI-research / follow-up lead.
 *   2. No affirmative assertion of securing/establishing approvals or arrangements remains.
 *   3. Any cited [Ref N] references in the complaint are retained and framed as
 *      screening inputs / conditional screening diligence rather than established approvals.
 */
export function isPhaseCumulativeAdditionComplaint(issueText: string): boolean {
  if (!issueText) return false;
  return (
    /\bcumulative\b/i.test(issueText) &&
    /\b(?:phase[- ]addition|addition\s+over\s+phase|addition\s+alongside\s+the\s+cumulative|both\s+cumulative\s+load\s+and\s+phase[- ]addition|cumulative\s+vs\.?\s+additional(?:\s+figures)?|cumulative\s+and\s+phase[- ]addition)\b/i.test(
      issueText,
    )
  );
}

function formatMwNum(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function escapeRegExpNum(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function splitPhase1AndPhase2SubSpans(normSent: string): { p1Span: string; p2Span: string } | null {
  let working = normSent;
  const colonPhase1 = /:\s*(?=for\s+phase\s*1\b)/.exec(working);
  if (colonPhase1) {
    working = working.slice(colonPhase1.index + colonPhase1[0].length);
  }

  const whereasMatch = /,\s*whereas\s+(?=(?:for\s+)?phase\s*2\b)/.exec(working);
  if (whereasMatch) {
    return {
      p1Span: working.slice(0, whereasMatch.index),
      p2Span: working.slice(whereasMatch.index + whereasMatch[0].length),
    };
  }

  const semiMatch = /;\s*(?=[^;()]*\bphase\s*2\b)/.exec(working);
  if (semiMatch) {
    return {
      p1Span: working.slice(0, semiMatch.index),
      p2Span: working.slice(semiMatch.index + semiMatch[0].length),
    };
  }

  const inPhase1Match = /\bin\s+phase\s*1\b/.exec(working);
  if (inPhase1Match) {
    const afterInPhase1 = working.slice(inPhase1Match.index + inPhase1Match[0].length);
    const andAfterP1 = /\band\b\s*(?=(?:for\s+|in\s+)?(?:phase\s*2\b|\d+(?:\.\d+)?\s*mw\b))/.exec(
      afterInPhase1,
    );
    if (andAfterP1) {
      const splitIdx = inPhase1Match.index + inPhase1Match[0].length + andAfterP1.index;
      return {
        p1Span: working.slice(0, splitIdx),
        p2Span: working.slice(splitIdx + andAfterP1[0].length),
      };
    }
  }

  const commaAndPhase2 = /,\s*(?:and|while)\s+(?=(?:for\s+|in\s+)?phase\s*2\b)/.exec(working);
  if (commaAndPhase2) {
    return {
      p1Span: working.slice(0, commaAndPhase2.index),
      p2Span: working.slice(commaAndPhase2.index + commaAndPhase2[0].length),
    };
  }

  const andPhase2 = /\band\s+(?=phase\s*2\b)/.exec(working);
  if (andPhase2) {
    return {
      p1Span: working.slice(0, andPhase2.index),
      p2Span: working.slice(andPhase2.index + andPhase2[0].length),
    };
  }

  return null;
}

function isPhaseCumulativeAdditionSatisfiedInText(
  issueText: string,
  finalReportText: string,
  report?: unknown,
): boolean {
  const fieldTexts = resolveTargetFieldTextsForReviewIssue(issueText, finalReportText, report);
  if (!fieldTexts || fieldTexts.length === 0) return false;

  const requires830Mw = /\bomitting\b[^.;]*\b830\s*mw\b/i.test(issueText);
  const requiresPhase1 =
    /\bphase\s*1\b[^.;]*\b(?:and|&)\s*(?:\d+(?:\.\d+)?\s*mw\s+in\s+)?phase\s*2\b/i.test(
      issueText,
    ) ||
    /\b\d+(?:\.\d+)?\s*mw\s+in\s+phase\s*1\s+and\s+\d+(?:\.\d+)?\s*mw\s+in\s+phase\s*2\b/i.test(
      issueText,
    );

  const p1NumsSet = new Set<string>();
  const cumulativeNumsSet = new Set<string>();
  const additionNumsSet = new Set<string>();

  for (const m of issueText.matchAll(
    /\bcumulative\b[^.;]*?\b(\d[\d,.]*)\s*mw\b|\b(\d[\d,.]*)\s*mw\b[^.;]{0,35}?\bcumulative\b/gi,
  )) {
    const val = (m[1] ?? m[2] ?? '').replace(/,/g, '');
    if (val) cumulativeNumsSet.add(val);
  }
  if (requires830Mw) {
    cumulativeNumsSet.add('830');
  }
  for (const m of issueText.matchAll(
    /\b(?:adds?|addition|additional|incremental)\b[^.;]*?\b(\d[\d,.]*)\s*mw\b|\b(\d[\d,.]*)\s*mw\b[^.;]{0,40}?\b(?:addition|additional|incremental)\b/gi,
  )) {
    const val = (m[1] ?? m[2] ?? '').replace(/,/g, '');
    if (val) additionNumsSet.add(val);
  }

  const pairMatch =
    /\b(\d[\d,.]*)\s*mw\s+in\s+phase\s*1\s+and\s+(\d[\d,.]*)\s*mw\s+in\s+phase\s*2\b/i.exec(
      issueText,
    );
  if (pairMatch) {
    const p1Val = Number(pairMatch[1]!.replace(/,/g, ''));
    const p2Val = Number(pairMatch[2]!.replace(/,/g, ''));
    if (Number.isFinite(p1Val) && p1Val > 0) {
      p1NumsSet.add(formatMwNum(p1Val));
    }
    if (Number.isFinite(p2Val) && p2Val > 0) {
      cumulativeNumsSet.add(formatMwNum(p2Val));
      if (Number.isFinite(p1Val) && p2Val > p1Val) {
        additionNumsSet.add(formatMwNum(p2Val - p1Val));
      }
    }
  }

  if (report && typeof report === 'object') {
    const repObj = report as Record<string, unknown>;
    const intent = repObj.projectIntentAtGen as
      | {
          phases?: Array<{ capacityMW?: number }>;
          pueValue?: number;
          pueTarget?: number;
          powerBasis?: string;
        }
      | undefined;
    if (intent && Array.isArray(intent.phases) && intent.phases.length >= 2) {
      const c1 = Number(intent.phases[0]?.capacityMW);
      const c2 = Number(intent.phases[1]?.capacityMW);
      const pue = Number(intent.pueValue ?? intent.pueTarget ?? 1.25);
      const basis = intent.powerBasis === 'it' ? 'it' : 'facility';
      if (Number.isFinite(c1) && c1 > 0 && Number.isFinite(c2) && c2 > c1) {
        const p1Fac = basis === 'facility' ? c1 : c1 * pue;
        const p1It = basis === 'facility' ? c1 / pue : c1;
        const p2Fac = basis === 'facility' ? c2 : c2 * pue;
        const p2It = basis === 'facility' ? c2 / pue : c2;
        p1NumsSet.add(formatMwNum(p1Fac));
        p1NumsSet.add(formatMwNum(p1It));
        cumulativeNumsSet.add(formatMwNum(p2Fac));
        cumulativeNumsSet.add(formatMwNum(p2It));
        additionNumsSet.add(formatMwNum(p2Fac - p1Fac));
        additionNumsSet.add(formatMwNum(p2It - p1It));
      }
    }
    const pf = repObj.phaseFeasibility as
      | {
          phase1?: { itLoadMw?: number; facilityLoadMw?: number };
          phase2?: { itLoadMw?: number; facilityLoadMw?: number };
        }
      | undefined;
    if (pf?.phase1 && pf?.phase2) {
      const p1Fac = Number(pf.phase1.facilityLoadMw);
      const p1It = Number(pf.phase1.itLoadMw);
      const p2Fac = Number(pf.phase2.facilityLoadMw);
      const p2It = Number(pf.phase2.itLoadMw);
      if (Number.isFinite(p1Fac) && p1Fac > 0) p1NumsSet.add(formatMwNum(p1Fac));
      if (Number.isFinite(p1It) && p1It > 0) p1NumsSet.add(formatMwNum(p1It));
      if (Number.isFinite(p2Fac) && p2Fac > 0) cumulativeNumsSet.add(formatMwNum(p2Fac));
      if (Number.isFinite(p2It) && p2It > 0) cumulativeNumsSet.add(formatMwNum(p2It));
      if (Number.isFinite(p1Fac) && Number.isFinite(p2Fac) && p2Fac > p1Fac) {
        additionNumsSet.add(formatMwNum(p2Fac - p1Fac));
      }
      if (Number.isFinite(p1It) && Number.isFinite(p2It) && p2It > p1It) {
        additionNumsSet.add(formatMwNum(p2It - p1It));
      }
    }
  }

  const p1Nums = Array.from(p1NumsSet);
  const cumulativeNums = Array.from(cumulativeNumsSet);
  const additionNums = Array.from(additionNumsSet);

  for (const fieldText of fieldTexts) {
    if (!fieldText || !fieldText.trim()) return false;
    if (requires830Mw && !/\b830\s*mw\b/i.test(fieldText)) {
      return false;
    }
    const sentences = splitIntoSentences(fieldText);
    if (sentences.length === 0) return false;

    let phase1LoadSentencesChecked = 0;
    let phase2LoadSentencesChecked = 0;

    for (const sent of sentences) {
      const normSent = normalize(sent).replace(/(\d),(\d)/g, '$1$2');
      if (!/\b\d+(?:\.\d+)?\s*mw\b/.test(normSent)) {
        continue;
      }

      const mentionsPhase1LoadMetric =
        requiresPhase1 &&
        /\bphase\s*1\b/.test(normSent) &&
        /\b(?:it\s+load|facility\s+load|facility\s+demand|it\s+target)\b/.test(normSent) &&
        (p1Nums.length === 0 ||
          p1Nums.some((n) => new RegExp(`\\b${escapeRegExpNum(n)}\\s*mw\\b`).test(normSent)));

      const mentionsPhase2LoadMetric =
        /\bphase\s*2\b/.test(normSent) &&
        (/\b(?:it\s+load|facility\s+load|cumulative|adds?\s+\d+(?:\.\d+)?\s*mw\s+(?:it|facility)|phase[- ]addition|facility\s+addition|it\s+addition)\b/.test(
          normSent,
        ) ||
          cumulativeNums.some((n) =>
            new RegExp(`\\b${escapeRegExpNum(n)}\\s*mw\\b`).test(normSent),
          ) ||
          additionNums.some((n) => new RegExp(`\\b${escapeRegExpNum(n)}\\s*mw\\b`).test(normSent)));

      if (!mentionsPhase1LoadMetric && !mentionsPhase2LoadMetric) continue;

      const splitSpans =
        mentionsPhase1LoadMetric && mentionsPhase2LoadMetric
          ? splitPhase1AndPhase2SubSpans(normSent)
          : null;

      if (mentionsPhase1LoadMetric) {
        if (mentionsPhase2LoadMetric && !splitSpans) {
          return false;
        }
        const p1Span = splitSpans ? splitSpans.p1Span : normSent;
        phase1LoadSentencesChecked++;
        const hasP1CumulativeCue = /\bcumulative\b/.test(p1Span);
        const hasP1AdditionCue = /\b(?:adds?|adding|addition|additional|incremental)\b/.test(
          p1Span,
        );
        if (!hasP1CumulativeCue || !hasP1AdditionCue) {
          return false;
        }
        if (
          p1Nums.length > 0 &&
          !p1Nums.some((n) => new RegExp(`\\b${escapeRegExpNum(n)}\\s*mw\\b`).test(p1Span))
        ) {
          return false;
        }
      }

      if (mentionsPhase2LoadMetric) {
        const p2Span = splitSpans ? splitSpans.p2Span : normSent;
        phase2LoadSentencesChecked++;
        const hasCumulativeCue = /\bcumulative\b/.test(p2Span);
        const hasAdditionCue = /\b(?:adds?|adding|addition|additional|incremental)\b/.test(p2Span);
        if (!hasCumulativeCue || !hasAdditionCue) {
          return false;
        }
        if (
          cumulativeNums.length > 0 &&
          !cumulativeNums.some((n) => new RegExp(`\\b${escapeRegExpNum(n)}\\s*mw\\b`).test(p2Span))
        ) {
          return false;
        }
        if (
          additionNums.length > 0 &&
          !additionNums.some((n) => new RegExp(`\\b${escapeRegExpNum(n)}\\s*mw\\b`).test(p2Span))
        ) {
          return false;
        }
      }
    }
    if (phase2LoadSentencesChecked === 0) {
      return false;
    }
    if (requiresPhase1 && phase1LoadSentencesChecked === 0) {
      return false;
    }
  }
  return true;
}

export function isReviewIssueAlreadyQualifiedInText(
  issueText: string,
  finalReportText: string,
  report?: unknown,
): boolean {
  if (!issueText || !finalReportText) return false;
  if (isPhaseCumulativeAdditionComplaint(issueText)) {
    return isPhaseCumulativeAdditionSatisfiedInText(issueText, finalReportText, report);
  }
  if (
    /\bcit(?:es?|ed|ing)\b[\s\S]*?\bwith\s+(?:a\s+)?['"`‘“]?\[verified\s+Ref\s+(?:\d+|N)\]['"`’”]?/i.test(issueText) &&
    /\binstead\s+of\b/i.test(issueText)
  ) {
    const refNums = [
      ...new Set(
        [...issueText.matchAll(/\[(?:verified\s+)?Ref\s+(\d+)\]/gi)].map((m) => m[1]!),
      ),
    ];
    if (
      refNums.length > 0 &&
      refNums.every(
        (n) =>
          !new RegExp(`\\[verified\\s+Ref\\s+${n}\\]`, 'i').test(finalReportText) &&
          new RegExp(`\\[(?:unverified|inferred)\\s+Ref\\s+${n}\\]`, 'i').test(finalReportText),
      )
    ) {
      return true;
    }
  }
  if (!/\bwithout\s+hedging\s+it\s+as\s+an?\s+unverified\b/i.test(issueText)) {
    return false;
  }
  const scopedText = resolveTargetTextForReviewIssue(issueText, finalReportText, report);
  if (!scopedText) return false;

  const token = extractOffendingToken(issueText);
  if (!token) return false;
  const haystack = normalize(scopedText);
  const normToken = normalize(token);
  if (!normToken || !haystack.includes(normToken)) return false;

  if (!areSecondaryComplaintObligationsSatisfied(issueText, scopedText)) {
    return false;
  }

  const requiresBothAiResearchAndFollowUp =
    /\bunverified\s+ai-research\s+follow-up\s+lead\b/i.test(issueText);

  const sentences = splitIntoSentences(scopedText);
  if (sentences.length === 0) return false;

  let totalOccurrencesChecked = 0;
  for (const sent of sentences) {
    const normSent = normalize(sent);
    if (!normSent.includes(normToken)) continue;
    if (hasAffirmativeApprovalOrArrangementAssertion(normSent)) {
      return false;
    }
    // Split on semicolons or ", and " when multiple clauses exist so a second
    // unqualified mention in a separate clause of the same sentence is caught.
    const clauses = normSent.split(/\s*;\s*|\s*,\s+and\s+/);
    for (const clause of clauses) {
      let searchFrom = 0;
      while (searchFrom < clause.length) {
        const idx = clause.indexOf(normToken, searchFrom);
        if (idx < 0) break;
        totalOccurrencesChecked++;
        const windowStart = Math.max(0, idx - 120);
        const windowEnd = Math.min(clause.length, idx + normToken.length + 120);
        const localWindow = clause.slice(windowStart, windowEnd);
        if (requiresBothAiResearchAndFollowUp) {
          if (
            !/\bunverified\b/.test(localWindow) ||
            !/\bai-research\b/.test(localWindow) ||
            !/\bfollow-up\b/.test(localWindow) ||
            !/\blead\b/.test(localWindow)
          ) {
            return false;
          }
        } else if (
          !/\bunverified\s+(?:ai-research|follow-up)\b/.test(localWindow) &&
          !/\bunverified\b[^.]{0,40}\blead\b/.test(localWindow)
        ) {
          return false;
        }
        searchFrom = idx + normToken.length;
      }
    }
  }
  return totalOccurrencesChecked > 0;
}

/**
 * Filter issues against the FINAL report text. Returns the surviving issues
 * plus the count dropped (for telemetry/stamping).
 */
export function filterSurvivingReviewIssues<T extends ReviewIssueLike>(
  issues: readonly T[],
  finalReportText: string,
  report?: unknown,
): { surviving: T[]; staleDropped: number } {
  const haystack = normalize(finalReportText ?? '');
  if (!haystack) return { surviving: [...issues], staleDropped: 0 };
  const surviving: T[] = [];
  let staleDropped = 0;
  for (const issue of issues) {
    const issueText =
      typeof issue?.issue === 'string' && issue.issue.trim().length > 0
        ? issue.issue
        : typeof issue?.description === 'string'
          ? issue.description
          : '';
    const scopedText = resolveTargetTextForReviewIssue(issueText, finalReportText, report);
    if (scopedText !== null) {
      const scopedHaystack = normalize(scopedText);
      const token = extractOffendingToken(issueText);
      const isQualificationComplaint =
        /\bwithout\s+hedging\s+it\s+as\s+an?\s+unverified\b/i.test(issueText) ||
        isPhaseCumulativeAdditionComplaint(issueText);
      if (token && !scopedHaystack.includes(normalize(token)) && !isQualificationComplaint) {
        if (areSecondaryComplaintObligationsSatisfied(issueText, scopedText)) {
          staleDropped++;
          continue;
        }
      }
      if (isReviewIssueAlreadyQualifiedInText(issueText, finalReportText, report)) {
        staleDropped++;
        continue;
      }
    }
    surviving.push(issue);
  }
  return { surviving, staleDropped };
}
